// Angular imports
import { HttpClient, HttpResponse } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

// CC-Common imports
import { IconConstants, RxjsUtils } from '@therealmanage/ciranet-ui-common';

// 3rd party imports
import { custom } from 'devextreme/ui/dialog';
import moment from 'moment-timezone';
import { plainToInstance } from 'class-transformer';

// Rxjs imports
import { Subject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { PrecisionService } from './precision.service';

// Model imports
import { NavigationInfo } from '../../_models/navigation-info';
import { CountryResultDto } from '../../_models/country-result-dto';
import { StateResultDto } from '../../_models/state-result-dto';
import { GetStatesParameterDto } from '../../_models/get-states-parameter-dto';

// Environment imports
import { environment } from '../../../environments/environment';

// Constant imports
import { Constants } from '../../_constants/constants';
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { SideNavigationMenus } from '../../_constants/side-navigation-menus';
import { RPCacheRegistrationService } from './rp-cache-registration-service';
import { CacheDuration } from '../../_constants/cache-duration';

// Service imports
import { EnvironmentConfigService } from '../environment-config.service';

/**
 * Result of {@link HelperService.showUrlInBrowserTab}. When the endpoint
 * returns a document it is opened in a new tab and `info` is undefined. When
 * the endpoint instead returns an expected informational message (e.g. an ACH
 * payment has no downloadable check image), no tab is opened and `info` holds
 * the message for the caller to surface.
 */
export interface BrowserTabResult {
  info?: string;
}

/**
 * Shown when a document endpoint returns an informational response with no
 * usable message, so a "view document" click always produces feedback instead
 * of silently doing nothing (RES-618).
 */
const NO_DOCUMENT_AVAILABLE_MESSAGE = 'No document is available for this item.';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  subjectAutoSelectHomeMenuItem = new Subject();
  subjectSideNavSizeChanged = new Subject();
  subjectCloseSideNav = new Subject<void>();
  subjectLaunchPreferences = new Subject<boolean>();
  subjectSelectProperty = new Subject();
  subjectLoginHeaderText = new Subject<string>();
  subjectIsRMLogoHidden = new Subject<boolean>();

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private sanitizer: DomSanitizer,
    private precisionService: PrecisionService,
    private cacheRegistrationService: RPCacheRegistrationService,
    private envConfig: EnvironmentConfigService
  ) {}

  isProduction(): boolean {
    return environment.production;
  }

  isLocalhost(): boolean {
    const url = window.location.href;
    return url.indexOf('localhost') >= 0;
  }

  showPreferences(): void {
    this.subjectLaunchPreferences.next(true);
  }

  hidePreferences(): void {
    this.subjectLaunchPreferences.next(false);
  }

  getDateString(myDate: Date): string {
    return formatDate(
      myDate,
      ValidationFormatConstants.dateFormat,
      Constants.localeEnglishUS
    );
  }

  getCurrencyStringFormat(value: number, precision: number = null): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    const formatter = new Intl.NumberFormat(Constants.localeEnglishUS, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits:
        precision !== null ? precision : this.precisionService.getPrecision(),
      maximumFractionDigits:
        precision !== null ? precision : this.precisionService.getPrecision()
    });
    return formatter.format(safeValue);
  }

  getConfirmationDialog(
    title: string,
    messageHtml: string,
    yesButtonTitle = 'Yes',
    noButtonTitle = 'No',
    yesButtonType: any = 'default',
    noButtonType: any = 'success'
  ) {
    const checkIcon = IconConstants.CONFIRM_YES;
    const crossIcon = IconConstants.CONFIRM_NO;
    const dlg = custom({
      title,
      messageHtml,
      buttons: [
        {
          text: yesButtonTitle,
          type: yesButtonType,
          icon: checkIcon,
          onClick: (e) => {
            return true;
          }
        },
        {
          text: noButtonTitle,
          icon: crossIcon,
          type: noButtonType,
          onClick: (e) => {
            return false;
          }
        }
      ]
    });
    return dlg;
  }

  getISODateString(myDate: Date): string {
    const date: string = formatDate(
      myDate,
      ValidationFormatConstants.dateISOFormat,
      Constants.localeEnglishUS
    );
    return date;
  }

  getUserTimezone(): string {
    return (
      localStorage.getItem(StorageKeyConstants.userTimeZoneKey) ||
      moment.tz.guess()
    );
  }

  isFromDateGreaterThenToDate(fromDate: Date, toDate: Date): boolean {
    const beginDate = moment(fromDate);
    const endDate = moment(toDate);
    return endDate.isAfter(beginDate);
  }

  getFileIconByExtension(fileName: string): string {
    let icon = 'fas fa-file-alt';
    if (
      !!fileName &&
      fileName.trim().length > 0 &&
      fileName.trim().indexOf('.') !== -1
    ) {
      const fileExt = fileName
        .trim()
        .toLowerCase()
        .substring(fileName.lastIndexOf('.') + 1);
      if (fileExt === 'pdf') {
        icon = 'fas fa-file-pdf cc-icon-pdf';
      } else if (['doc', 'docx'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-file-word cc-icon-docx';
      } else if (['xls', 'xlsx'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-file-excel cc-icon-xlsx';
      } else if (['ppt', 'pptx'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-file-powerpoint cc-icon-pptx';
      } else if (['jpg', 'jpeg'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-image cc-icon-jpg';
      } else if (['png'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-image cc-icon-png';
      } else if (['gif'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-image cc-icon-gif';
      } else if (['bmp'].indexOf(fileExt) !== -1) {
        icon = 'fas fa-image cc-icon-gif';
      }
    }
    return icon;
  }

  downloadFileUsingBlob(fileData: Blob, fileName: string): void {
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(fileData, fileName);
    } else {
      const downloadURL = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = downloadURL;
      link.setAttribute('download', fileName);
      if (typeof link.download === 'undefined') {
        link.target = '_blank';
      }
      document.body.appendChild(link);
      link.click();
      // Remove link & Revoke object URL
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadURL);
      }, 2500);
    }
  }

  showUrlInBrowserTab(
    fileUrl: string,
    fileName: string
  ): Observable<BrowserTabResult> {
    return new Observable((observer) => {
      this.httpClient
        .get(fileUrl, { observe: 'response', responseType: 'arraybuffer' })
        .subscribe(
          (resp: HttpResponse<ArrayBuffer>) => {
            const headers = resp.headers;
            const type = headers.get('content-type');
            // An informational JSON payload (e.g. an ACH payment has no
            // downloadable check image) must be surfaced to the caller, not
            // opened as a blank tab. Detect it by content-type; only when NO
            // content-type is present at all, fall back to sniffing the body. A
            // present non-JSON content-type is authoritative and opens as a
            // document (RES-618).
            const info = this.extractInfoMessage(resp.body, type);
            if (info !== undefined) {
              observer.next({ info });
              observer.complete();
              return;
            }
            const blob = new Blob([resp.body], { type });
            const blobUrl = window.URL.createObjectURL(blob);
            const win = window.open(blobUrl, '_blank');
            try {
              win.focus();
            } catch (e) {
              const dialog = this.getOkDialog(
                'Error',
                'Looks like Pop-up Blocker is enabled! Please add ciranet.com domain to the exception list.'
              );
              dialog.show();
            }
            setTimeout(() => {
              try {
                // Possible DOMException in Firefox while accessing win.document.
                // [DEV-4034]: "Permission denied to access property" will occure on Firefox browsers due to
                // violation the same-origin policy as blob urls will be treated differently
                if (!!win && !!win.document) {
                  win.document.title = fileName;
                }
              } catch (e) {
                // Ignore this firefox error.
              }
            }, 1000);
            // Remove link & Revoke object URL
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2500);
            observer.next({});
            observer.complete();
          },
          (error) => {
            observer.error(error);
            observer.complete();
          }
        );
    });
  }

  /**
   * Detects an informational JSON payload returned by a document endpoint and
   * extracts its message. A response is treated as informational when its
   * content-type is JSON, or — only when NO content-type is present at all — its
   * body sniffs as a JSON object (guarding against a missing content-type on the
   * info 200, RES-618). A present non-JSON content-type is authoritative and
   * always treated as a document, so document formats that also lead with '{'
   * (e.g. RTF) are never misclassified. Returns the message, a generic fallback
   * when the payload is JSON but carries no message (so a click never
   * dead-ends), or undefined for a binary document (which the caller opens in a
   * tab).
   */
  private extractInfoMessage(
    body: ArrayBuffer | null,
    contentType: string | null
  ): string | undefined {
    // Media types are case-insensitive (RFC 7231 §3.1.1.1) — normalize before
    // matching so e.g. `Application/Json` is not misread as a document.
    const ct = contentType?.toLowerCase() ?? null;
    if (!body || body.byteLength === 0) {
      // An empty body with a JSON content-type is still a (degenerate) info
      // response — surface the fallback rather than opening a blank tab (RES-618).
      return ct?.includes('application/json')
        ? NO_DOCUMENT_AVAILABLE_MESSAGE
        : undefined;
    }
    const isJson =
      (ct?.includes('application/json') ?? false) ||
      // A present non-JSON content-type (application/pdf, image/png,
      // application/rtf, …) is authoritative — it's a document. Only fall back
      // to the byte sniff when the info 200 arrives with no content-type at all.
      (ct == null && this.looksLikeJsonObject(body));
    if (!isJson) {
      return undefined;
    }
    try {
      const json = JSON.parse(new TextDecoder('utf-8').decode(body));
      // Coalesce on emptiness, not just nullishness: an empty/whitespace-only
      // message would be falsy at the call site's `if (result.info)` guard and
      // produce a silent dead click — the exact case the fallback prevents.
      const message = (json.message ?? json.info)?.toString().trim();
      return message ? message : NO_DOCUMENT_AVAILABLE_MESSAGE;
    } catch {
      // Declared/shaped as JSON but unparseable — show generic feedback rather
      // than a silent dead click (RES-618).
      return NO_DOCUMENT_AVAILABLE_MESSAGE;
    }
  }

  /**
   * Cheap structural sniff used ONLY when a response carries no content-type at
   * all: true when the first non-whitespace byte (after an optional UTF-8 BOM)
   * is '{'. A present content-type is always authoritative (see
   * extractInfoMessage), so document formats that also lead with '{' (e.g. RTF's
   * `{\rtf`) never reach this sniff.
   */
  private looksLikeJsonObject(body: ArrayBuffer): boolean {
    const bytes = new Uint8Array(body);
    // Skip an optional UTF-8 BOM as an atomic 3-byte sequence, so a lone 0xEF
    // in a binary payload is never swallowed as if it were a BOM.
    let start = 0;
    if (
      bytes.length >= 3 &&
      bytes[0] === 0xef &&
      bytes[1] === 0xbb &&
      bytes[2] === 0xbf
    ) {
      start = 3;
    }
    for (let i = start; i < bytes.length; i++) {
      const b = bytes[i];
      // skip space, tab, LF, CR
      if (b === 0x20 || b === 0x09 || b === 0x0a || b === 0x0d) {
        continue;
      }
      return b === 0x7b; // '{'
    }
    return false;
  }

  getCountries(clearCache?: boolean): Observable<CountryResultDto[]> {
    const url = this.envConfig.identityApiUrl + 'common/countries';
    this.cacheRegistrationService.registerForCache(url, clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers };
    return this.httpClient
      .get<any[]>(url, options)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(CountryResultDto, responseData)
        )
      );
  }

  getStates(param: GetStatesParameterDto): Observable<StateResultDto[]> {
    const url =
      this.envConfig.identityApiUrl + 'common/states' + param.getQueryString();
    return this.httpClient
      .get<any[]>(url)
      .pipe(
        map((responseData: any[]) =>
          plainToInstance(StateResultDto, responseData)
        )
      );
  }

  navigateToRoute(info: NavigationInfo): void {
    this.subjectSelectProperty.next(info);
    if (!!info.navCode) {
      const menuItems = SideNavigationMenus.propertySubMenuItems
        .concat(SideNavigationMenus.paymentSubMenuItems)
        .concat(SideNavigationMenus.communitySubMenuItems)
        .concat(SideNavigationMenus.buttonMenuItems)
        .concat(SideNavigationMenus.bottomMenuItems);
      const menu = menuItems.find(
        (item) =>
          item.name.toLowerCase() === info.navCode.toLowerCase() ||
          item.path === info.navCode
      );
      if (!!menu && !!menu.path) {
        const queryParams = !!info.view
          ? { queryParams: { view: info.view } }
          : undefined;
        this.router.navigate([menu.path], queryParams);
      }
    }
  }

  getImageBlobFromUrl(url: string): Observable<Blob> {
    return this.httpClient
      .get(url, { responseType: 'arraybuffer' })
      .pipe(map((responseData: any) => new Blob([responseData])));
  }

  getImageSafeUrlFromUrl(url: string): Observable<string | SafeUrl> {
    return Observable.create((observer) => {
      RxjsUtils.subscribeToObservable(
        this.getImageBlobFromUrl(url),
        (imageData: Blob) => {
          let returnUrl: string | SafeUrl;
          try {
            const imageUrl = window.URL.createObjectURL(imageData);
            returnUrl = this.sanitizer.bypassSecurityTrustUrl(imageUrl);
            observer.next(returnUrl);
          } catch (e) {
            console.error(`Failed to use URL.createObjectURL: ${e}`);
            // Fallback if createObjectURL is not supported
            const fileReader = new FileReader();
            fileReader.onload = () => {
              returnUrl = this.sanitizer.bypassSecurityTrustUrl(
                fileReader.result.toString()
              );
              observer.next(returnUrl);
            };
            fileReader.onerror = (e) => {
              console.error(`File not supported: ${e}`);
              observer.error(e);
            };
            fileReader.readAsDataURL(imageData);
          } finally {
            observer.complete();
          }
        }
      );
    });
  }

  sortSequenceItems(list: Array<any>): Array<any> {
    return (list || []).sort((x, y) => {
      x.sequence = !!x.sequence ? x.sequence : 9999;
      y.sequence = !!y.sequence ? y.sequence : 9999;
      if (x.sequence > y.sequence) {
        return 1;
      } else if (x.sequence < y.sequence) {
        return -1;
      }
      return 0;
    });
  }

  getHyperlinkText(text: string): string {
    return text.replace(ValidationFormatConstants.webUrlPattern, (url) => {
      return '<a target="_blank" href="' + url + '">' + url + '</a>';
    });
  }

  getOkDialog(
    title: string,
    messageHtml: string,
    buttonText = 'OK',
    buttonType: any = 'default'
  ) {
    return custom({
      title,
      messageHtml,
      buttons: [
        {
          type: buttonType,
          text: buttonText,
          hint: buttonText,
          onClick: (e) => {
            return true;
          }
        }
      ]
    });
  }
}
